// token-stream-output — code block that renders streaming LLM token output
// with a blinking cursor at the end. Swaps to final committed text on
// step_text_complete (no cursor). Reconnect clears liveText → rendered via
// REST-hydrated outputText from the parent step.
// Optional refs prop renders KB/KG link-icon strip post-stream (A3 future).

import { cn } from "@/utils";
import type { KbRef, KgRef } from "@/types/workflow.types";
import { RefLinkStrip } from "../run-detail/ref-link-strip";

interface TokenStreamOutputProps {
  /** Live in-progress text accumulated from token_delta payloads. */
  liveText?: string;
  /** Final committed text from step_text_complete or step.outputData.text. */
  finalText?: string;
  /** When true, show caret blink at the end of liveText. */
  isStreaming?: boolean;
  /** KB/KG refs from step_text_complete payload — absent until BE A3 ships. */
  refs?: { kb?: KbRef[]; kg?: KgRef[] };
  className?: string;
}

/**
 * TokenStreamOutput — shows live text with blinking caret during streaming.
 * Once isStreaming=false or finalText is present (from step_text_complete),
 * renders finalText without caret. Caret keyframe is defined in utilities.css.
 * Ref strip renders below code block when stream is complete and refs present.
 */
export function TokenStreamOutput({
  liveText,
  finalText,
  isStreaming = false,
  refs,
  className,
}: TokenStreamOutputProps) {
  const displayText = isStreaming ? (liveText ?? "") : (finalText ?? liveText ?? "");
  const showRefs = !isStreaming && (refs?.kb?.length || refs?.kg?.length);

  if (!displayText && !isStreaming) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <pre
        className={cn(
          "text-[11px] leading-relaxed font-mono",
          "bg-[var(--muted)] rounded-[var(--radius-md)] px-3 py-2",
          "text-[var(--foreground)] whitespace-pre-wrap break-words",
          "max-h-[160px] overflow-y-auto custom-scrollbar",
          className
        )}
      >
        {displayText}
        {isStreaming && (
          <span
            aria-hidden
            className="inline-block w-[7px] h-[13px] bg-[var(--primary)] ml-0.5 align-text-bottom animate-[caret_1s_steps(1,end)_infinite]"
          />
        )}
      </pre>

      {/* KB / KG ref strip — only rendered once stream completes + refs present */}
      {showRefs && <RefLinkStrip kbRefs={refs?.kb} kgRefs={refs?.kg} />}
    </div>
  );
}
